// Reusable Zod primitives (architecture.md §3 ADR-3, §2 regional defaults).
import { z } from 'zod';
import { SUPPORTED_LOCALES } from '../enums.js';

export const uuid = z.string().uuid();

export const email = z.string().trim().toLowerCase().email().max(320);

// E.164 phone (India examples use +91). Optional '+' then 8–15 digits, no leading 0.
export const phoneE164 = z
  .string()
  .trim()
  .regex(/^\+?[1-9]\d{7,14}$/, 'Must be a valid E.164 phone number');

// citext columns: usernames, slugs, promo codes, domains. Case-insensitive; store lower.
export const username = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(40)
  .regex(/^[a-z0-9][a-z0-9-_]*[a-z0-9]$/, 'Letters, numbers, - and _ only');

export const slug = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'Lowercase letters, numbers and hyphens only');

export const promoCodeString = z
  .string()
  .trim()
  .toUpperCase()
  .min(2)
  .max(40)
  .regex(/^[A-Z0-9-_]+$/, 'Letters, numbers, - and _ only');

// IANA timezone name. Validated against the runtime tz database when available.
export const timezone = z.string().trim().min(1).max(64).refine((tz) => {
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}, 'Unknown IANA timezone');

export const locale = z.enum(SUPPORTED_LOCALES);

export const currency = z.string().trim().toUpperCase().length(3);

// Money is ALWAYS integer minor units (paise). Never floats. (§2)
export const moneyMinor = z.number().int().min(0);

export const hhmm = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be HH:MM (24h)');

export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');

export const isoDateTime = z.string().datetime({ offset: true });

export const dayOfWeek = z.number().int().min(0).max(6); // 0 = Sunday
